/**
 * T-054: composite fleet status card pinned above the Pocket settings
 * tabs. One verdict (healthy/degraded/down) rolled up from 4 sub-signals
 * with a 30s auto-refresh and a collapsible recent-Pocket-activity list.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { getFleetStatus } from '@/lib/pocketSettingsApi';
import type {
  PocketFleetSignal,
  PocketFleetSignalStatus,
  PocketFleetStatus,
  PocketFleetVerdict,
} from '@/types/pocketSettings';

const POLL_INTERVAL_MS = 30_000;

const VERDICT_TONE: Record<
  PocketFleetVerdict,
  { dot: string; pill: string; label: string; summary: string }
> = {
  ok: {
    dot: 'bg-emerald-500',
    pill: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
    label: 'Healthy',
    summary: 'All Pocket signals are green.',
  },
  degraded: {
    dot: 'bg-amber-500',
    pill: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
    label: 'Degraded',
    summary: 'One or more signals need attention.',
  },
  down: {
    dot: 'bg-red-500',
    pill: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',
    label: 'Down',
    summary: 'A critical signal is failing — investigate now.',
  },
};

const SIGNAL_TONE: Record<PocketFleetSignalStatus, string> = {
  ok: 'bg-emerald-500',
  degraded: 'bg-amber-500',
  down: 'bg-red-500',
};

const SIGNAL_STATUS_LABEL: Record<PocketFleetSignalStatus, string> = {
  ok: 'OK',
  degraded: 'Degraded',
  down: 'Down',
};

interface SignalTileProps {
  label: string;
  signal: PocketFleetSignal;
  format?: (value: number) => string;
  hint?: string;
}

function SignalTile({ label, signal, format, hint }: SignalTileProps) {
  const display = format ? format(signal.value) : String(signal.value);
  const statusLabel = SIGNAL_STATUS_LABEL[signal.status];
  return (
    <div
      className="rounded-md border bg-background/40 px-3 py-2"
      aria-label={`${label}: ${statusLabel}, ${display}`}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={cn('h-2 w-2 rounded-full', SIGNAL_TONE[signal.status])}
        />
        <span className="sr-only">{statusLabel}</span>
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="mt-1 font-mono tabular-nums text-lg leading-tight text-foreground">
        {display}
      </div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function shortUserAgent(ua: string | null): string {
  if (!ua) return '—';
  const match = ua.match(/HTPocket\/[^\s;]+/i);
  return match ? match[0] : ua.slice(0, 32);
}

export function FleetStatusComposite() {
  const [status, setStatus] = useState<PocketFleetStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRecent, setShowRecent] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Sequence token: prevents a slow in-flight refresh (stale) from
  // overwriting a faster, newer one (fresh). The latest issued token wins;
  // earlier ones drop their result on the floor.
  const refreshSeq = useRef(0);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    const seq = ++refreshSeq.current;
    try {
      const next = await getFleetStatus();
      if (!mounted.current || seq !== refreshSeq.current) return;
      setStatus(next);
      setError(null);
    } catch (err) {
      if (!mounted.current || seq !== refreshSeq.current) return;
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      if (mounted.current && seq === refreshSeq.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    refreshTimer.current = setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      mounted.current = false;
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [refresh]);

  const verdict: PocketFleetVerdict = status?.verdict ?? 'ok';
  const tone = VERDICT_TONE[verdict];
  const checkedAt = status?.checked_at ? formatRelative(status.checked_at) : '—';

  return (
    <section
      data-testid="fleet-status-composite"
      className="rounded-lg border bg-card p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className={cn('h-3 w-3 rounded-full', tone.dot, loading && 'animate-pulse')}
          />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                Fleet status
              </span>
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                  tone.pill,
                )}
              >
                {loading && !status ? 'Checking…' : tone.label}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {error
                ? `Refresh failed: ${error}`
                : status
                ? tone.summary
                : 'Loading rollup…'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>Updated {checkedAt}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setLoading(true);
              refresh();
            }}
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {status && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <SignalTile
            label="Outbox DLQ"
            signal={status.signals.outbox_dlq}
            hint="Sales sync failures (permanent)"
          />
          <SignalTile
            label="In-flight idem keys"
            signal={status.signals.idempotency_inflight}
            hint="Pending check-in submissions"
          />
          <SignalTile
            label="Historical-scan rate"
            signal={status.signals.historical_scan_rate_pct}
            format={(v) => `${v.toFixed(1)}%`}
            hint="Old-sticker fallbacks (7d)"
          />
          <SignalTile
            label="Pocket users (24h)"
            signal={status.signals.pocket_active_users_24h}
            hint="Distinct user_ids from Pocket"
          />
        </div>
      )}

      {status && status.recent_pocket_checkins.length > 0 && (
        <div className="mt-3 border-t pt-3">
          <button
            type="button"
            onClick={() => setShowRecent((v) => !v)}
            aria-expanded={showRecent}
            aria-controls="fleet-status-recent-activity"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {showRecent ? (
              <ChevronUp className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            )}
            Recent Pocket activity ({status.recent_pocket_checkins.length})
          </button>
          {showRecent && (
            <div
              id="fleet-status-recent-activity"
              className="mt-2 overflow-x-auto"
            >
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr className="text-left">
                    <th className="pb-1 pr-3 font-medium">When</th>
                    <th className="pb-1 pr-3 font-medium">Event</th>
                    <th className="pb-1 pr-3 font-medium">User-Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {status.recent_pocket_checkins.map((row) => (
                    <tr key={row.id} className="border-t">
                      <td className="py-1 pr-3 font-mono tabular-nums text-muted-foreground">
                        {formatRelative(row.event_timestamp)}
                      </td>
                      <td className="py-1 pr-3">{row.event_type}</td>
                      <td className="py-1 pr-3 font-mono text-muted-foreground">
                        {shortUserAgent(row.user_agent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
